/* eslint-disable @typescript-eslint/no-use-before-define */
import { el, sv, VNode } from "@dhx/ts-common/dom";
import { IFlowShapeConfig, IBaseShape } from "@dhx/ts-diagram";
import { BaseShape } from "./BaseShape";
import { getCircleTpl, getShapeCss, getTextTemplate } from "../templates/templates";
import { getMeta } from "../../helpers/baseMetaInfo";
import locale from "../../locales/en";

export class FlowShape extends BaseShape implements IBaseShape {
	public config: IFlowShapeConfig;

	constructor(config: IFlowShapeConfig, parameters?) {
		super(config, parameters);
	}
	public getMetaInfo(): { [key: string]: string }[] {
		return getMeta([
			{ type: "grid", label: locale.gridStep },
			{ type: "arrange", label: locale.arrange },
			{ type: "fill", label: locale.fill },
			{ type: "text", label: locale.text },
			{ type: "strokeProps", label: locale.stroke },
			{ type: "textProps", label: locale.textProps },
		]);
	}
	protected setDefaults(config: IFlowShapeConfig, defaults?: IFlowShapeConfig): IFlowShapeConfig {
		const {
			width,
			height,
			stroke,
			extraLinesStroke,
			fill,
			strokeWidth,
			fontColor,
			strokeDash,
			textAlign,
			lineHeight,
			fontStyle,
			textVerticalAlign,
			type,
			fontSize,
			text,
			preview,
			x,
			y,
			fixed,
			editable,
		} = config;

		const linesStroke = type === "roll" ? "#DEDEDE" : extraLinesStroke || "#FFF";
		config.extraLinesStroke = defaults.extraLinesStroke || linesStroke;

		const circularShapes = ["circle", "or", "junction"];
		const isCircular = circularShapes.includes(type);

		const widthDefaut = defaults.width ? parseFloat(defaults.width as any) : isCircular ? 90 : 140;
		const heightDefault = defaults.height ? parseFloat(defaults.height as any) : 90;
		const lineHeightDefault = defaults.lineHeight ? parseFloat(defaults.lineHeight as any) : 14;
		const fontSizeDefault = defaults.fontSize ? parseFloat(defaults.fontSize as any) : 14;
		const strokeWidthDefault = defaults.strokeWidth ? parseFloat(defaults.strokeWidth as any) : 1;

		config.strokeWidth = strokeWidth || strokeWidthDefault;
		config.width = width || widthDefaut;
		config.height = height || heightDefault;
		config.fontSize = fontSize || fontSizeDefault;
		config.lineHeight = lineHeight || lineHeightDefault;
		config.strokeDash = strokeDash || defaults.strokeDash || "";
		config.fill = fill || defaults.fill || "#DEDEDE";
		config.stroke = stroke || defaults.stroke || "#DEDEDE";
		config.text = typeof text === "string" ? text : defaults.text || "";
		config.textAlign = textAlign || defaults.textAlign || "center";
		config.textVerticalAlign = textVerticalAlign || defaults.textVerticalAlign || "center";
		config.fontStyle = fontStyle || defaults.fontStyle || "normal";
		config.fontColor = fontColor || defaults.fontColor || "#4C4C4C";
		config.preview = preview || defaults.preview;

		config.x = x || 0;
		config.y = y || 0;

		config.fixed = fixed || false;
		config.editable = typeof editable === "boolean" ? editable : true;

		return config;
	}
	protected getContent(): VNode[] {
		const { text, $editable } = this.config;
		return [
			el(
				"div",
				{
					class: "shape_content dhx_diagram_item__text ",
					style: getShapeCss(this.config),
				},
				$editable ? [this.getEditorNode()] : text
			),
		];
	}
	public render(): VNode {
		if (!this.config.strokeType) this.config.strokeType = "line";
		if (this.config.strokeType === "line") {
			this.config.strokeDash = "0";
		}
		if (this.config.strokeType === "dash") {
			this.config.strokeDash = "5,5";
		}
		if (this.config.strokeType === "none") {
			this.config.stroke = "none";
		}
		const { id, angle, width, height, strokeWidth, $group, $selected } = this.config;
		const zIndex = $group || $selected ? ($selected ? 2 : 1) : 0;
		const coords = this.getCoords(this.config);
		const strokeRatio = strokeWidth / 2;

		const circle = [];
		if (Array.isArray(this.config.dir)) {
			this.config.dir.forEach((_dir, index) => {
				circle.push(getCircleTpl(this.config, index));
			});
		} else {
			circle.push(getCircleTpl(this.config));
		}

		return el(
			"div",
			{
				_key: id,
				class: "dhx_diagram_item dhx_diagram_shape dhx_diagram_flow_item " + this.getCss(),
				dhx_id: id,
				style: {
					transform: `rotate(${angle || 0}deg)`,
					position: "absolute",
					height,
					width,
					top: coords.y,
					left: coords.x,
					zIndex,
				},
			},
			[
				sv(
					"svg",
					{
						xmlns: "http://www.w3.org/2000/svg",
						height,
						width,
						viewBox: `${-strokeRatio} ${-strokeRatio} ${width + strokeRatio} ${height +
							strokeRatio}`,
						reserveAspectRatio: "none",
					},
					this._getShapeContour()
				),
				getTextTemplate(this.config, this.getContent()),
				...circle,
			]
		);
	}
	private _getShapeContour(): VNode[] {
		const { width, height, stroke, fill, strokeWidth, strokeDash, extraLinesStroke, type } = this.config;
		const shape = flowShapes[type];

		const part = Math.round(width / 12);
		const pathWidth = width - strokeWidth / 2;
		const pathHeight = height - strokeWidth / 2;
		const path = shape.path(pathWidth, pathHeight, part);
		const additionalPath = shape.additionalPath(pathWidth, pathHeight, part);

		const getPathElement = d => {
			return sv("path", {
				d: d,
				class: "dhx_diagram_flow_shape ",
				stroke: stroke,
				fill: fill,
				"stroke-width": strokeWidth,
				"stroke-dasharray": strokeDash,
			});
		};
		const getAdditionalPathElement = d => {
			return sv("path", {
				d,
				fill: "none",
				stroke: extraLinesStroke,
				class: "dhx_diagram_extra_lines",
			});
		};
		return [getPathElement(path), getAdditionalPathElement(additionalPath)];
	}
}
export const flowShapes = {
	circle: {
		path: (width: number, height: number) => {
			return `
			M ${width / 2} 0 A ${height / 2},${height / 2} 0 1 0 ${width / 2},${height}
			A ${height / 2},${height / 2} 0 1 0 ${width / 2},0 Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	rectangle: {
		path: (width: number, height: number) => {
			return `M 0,0 L 0,${height} L ${width},${height} L ${width},0 Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	triangle: {
		path: (width: number, height: number) => {
			return `M ${width / 2} 0 L${width} ${height} L 0 ${height} z`;
		},
		additionalPath: () => {
			return;
		},
	},
	start: {
		path: (width: number, height: number) => {
			return `
			M ${height / 2} 0 A ${height / 2},${height / 2} 0 1 0 ${height / 2},${height}
			H ${width - height / 2} A ${height / 2},${height / 2} 0 1 0 ${width - height / 2},0 H ${height / 2} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	end: {
		path: (width: number, height: number) => {
			return `
			M ${height / 2} 0 A ${height / 2},${height / 2} 0 1 0 ${height / 2},${height}
			H ${width - height / 2} A ${height / 2},${height / 2} 0 1 0 ${width - height / 2},0 H ${height / 2} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	process: {
		path: (width: number, height: number) => {
			return `M 0,0 L 0,${height} L ${width},${height} L ${width},0 Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	output: {
		path: (width: number, height: number, part: number) => {
			return `M ${part * 2},0 ${width},0 ${width - part * 2},${height} 0,${height} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	decision: {
		path: (width: number, height: number) => {
			return `M 0 ${height / 2} L ${width / 2} 0 L ${width} ${height / 2} L ${width / 2} ${height} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	display: {
		path: (width: number, height: number) => {
			return `
			M 0 ${height / 2} L ${width / 4} 0 H ${(width * 3) / 4}
			A ${width / 4},${height / 2} 0 1 1 ${(width * 3) / 4},${height}
			H ${width / 4} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	document: {
		path: (width: number, height: number) => {
			height -= 8;
			return `M0 ${height} C
			${width / 6} ${(height * 10) / 9},
			${width / 3} ${(height * 10) / 9},
			${width / 2} ${height} S
			${(width * 5) / 6} ${(height * 8) / 9},
			${width} ${height}
			V 0 H 0 Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	data: {
		path: (width: number, height: number, part: number) => {
			return `M ${part} 0 Q
			${-part} ${height / 2},
			${part} ${height} H ${width} Q
			${width - part * 2} ${height / 2},
			${width} 0 Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	database: {
		path: (width: number, height: number, part: number) => {
			return `M 0 ${part} A ${width / 2},${part} 0 1 0 ${width},${part}
			A ${width / 2},${part} 0 1 0 0,${part}
			V ${height} H ${width} V ${part}`;
		},
		additionalPath: (width: number, height: number, part: number) => {
			return `M 0 ${part} A ${width / 2},${part} 0 1 0 ${width},${part}`;
		},
	},
	internal: {
		path: (width: number, height: number) => {
			return `M 0,0 L 0,${height} L ${width},${height} L ${width},0 Z`;
		},
		additionalPath: (width: number, height: number, part: number) => {
			return `M ${part} 0 V ${height} M 0 ${part} H ${width}`;
		},
	},
	offline: {
		path: (width: number, height: number) => {
			return `M 0,0 ${width},0 ${width / 2},${height} Z`;
		},
		additionalPath: (width: number, height: number, part: number) => {
			const hypotenuse = Math.sqrt((width / 2) ** 2 + height ** 2);
			const coef = height / hypotenuse;
			const cathet = Math.sqrt((part / coef) ** 2 - part ** 2);
			return `M ${width / 2 - cathet} ${height - part} h ${2 * cathet}`;
		},
	},
	delay: {
		path: (width: number, height: number) => {
			return `
			M 0 0 H ${(width * 3) / 4}
			A ${width / 4},${height / 2} 0 1 1 ${(width * 3) / 4},${height}
			H 0 Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	page: {
		path: (width: number, height: number) => {
			return `
			M 0,0
			${width},0
			${width},${height / 2}
			${width / 2},${height}
			0,${height / 2} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	input: {
		path: (width: number, height: number) => {
			return `
			M 0,${height / 3}
			${width},${0}
			${width},${height}
			0,${height} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	operation: {
		path: (width: number, height: number) => {
			return `
			M 0,0
			${width},0
			${(width * 3) / 4},${height}
			${width / 4},${height} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	punchcard: {
		path: (width: number, height: number) => {
			return `
			M 0,${height / 4}
			${width / 4},0
			${width},0
			${width},${height}
			0,${height} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	subroutine: {
		path: (width: number, height: number) => {
			return `M 0,0 L 0,${height} L ${width},${height} L ${width},0 Z`;
		},
		additionalPath: (width: number, height: number, part: number) => {
			return `M ${part} 0 V ${height} M ${width - part} 0 V ${height}`;
		},
	},
	comment: {
		path: (width: number, height: number) => {
			const w = 4;
			return `M ${width} 0 H 0 V ${height} H${width} V${height - w} H${w} V${w} H${width}`;
		},
		additionalPath: () => {
			return;
		},
	},
	storage: {
		path: (width: number, height: number) => {
			return `M 0,0 ${width},0 ${width / 2},${height} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	extract: {
		path: (width: number, height: number) => {
			return `M 0,${height} ${width},${height} ${width / 2},0 Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	collate: {
		path: (width: number, height: number) => {
			return `M ${width / 2} ${height / 2} L 0 0 H ${width} L 0 ${height} H ${width} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	or: {
		path: (width: number, height: number) => {
			return `
			M ${width / 2} 0 A ${height / 2},${height / 2} 0 1 0 ${width / 2},${height}
			A ${height / 2},${height / 2} 0 1 0 ${width / 2},0 Z`;
		},
		additionalPath: (width: number, height: number) => {
			return `
			M${(width - height) / 2} ${height / 2} ${width - (width - height) / 2} ${height / 2}
			M${width / 2} ${0} ${width / 2} ${height}`;
		},
	},
	junction: {
		path: (width: number, height: number) => {
			return `
			M ${width / 2} 0 A ${height / 2},${height / 2} 0 1 0 ${width / 2},${height}
			A ${height / 2},${height / 2} 0 1 0 ${width / 2},0 Z`;
		},
		additionalPath: (width: number, height: number) => {
			return `
			M ${width / 2 - (height * Math.SQRT2) / 4} ${height / 2 - (height * Math.SQRT2) / 4} L ${width / 2 +
				(height * Math.SQRT2) / 4} ${height / 2 + (height * Math.SQRT2) / 4}
			M ${width / 2 - (height * Math.SQRT2) / 4} ${height / 2 + (height * Math.SQRT2) / 4} L ${width / 2 +
				(height * Math.SQRT2) / 4} ${height / 2 - (height * Math.SQRT2) / 4}`;
		},
	},
	keyring: {
		path: (width: number, height: number, part: number) => {
			return `
			M ${part} 0 A ${part},${height / 2} 0 1 0 ${part},${height}
			H ${width - part} A ${part},${height / 2} 0 1 0 ${width - part},0 H ${part} Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	tape: {
		path: (width: number, height: number) => {
			return `
			M0 ${height - 12} C
			${width / 6} ${((height - 8) * 10) / 9},
			${width / 3} ${((height - 8) * 10) / 9},
			${width / 2} ${height - 8} S
			${(width * 5) / 6} ${((height - 8) * 8) / 9},
			${width} ${height}
			V 12 C
			${(width * 5) / 6} ${-height / 9},
			${(width * 2) / 3} ${height / 9 / 2},
			${width / 2} 8 S
			${width / 6} ${height / 9},
			0 0 Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	preparation: {
		path: (width: number, height: number) => {
			const w = 20;
			return `M0 ${height / 2}L${w} 0H${width - w}L ${width} ${height / 2}L${width -
				w} ${height}H${w}L0 ${height / 2}Z`;
		},
		additionalPath: () => {
			return;
		},
	},
	endpoint: {
		path: (width: number, height: number) => {
			return `M0 ${height / 2}  L${width / 2} ${0} L ${width / 2} ${height} z`;
		},
		additionalPath: () => {
			return;
		},
	},
	roll: {
		path: (width: number, height: number) => {
			return `
			M ${width / 2} 0 A ${height / 2},${height / 2} 0 1 0 ${width / 2},${height}
			A ${height / 2},${height / 2} 0 1 0 ${width / 2},0 Z`;
		},
		additionalPath: (width: number, height: number) => {
			return `M ${width / 2} ${height} H ${width}`;
		},
	},
};
